// The vault is what stays on the device.
//
// The server keeps a document's dates and a masked number and nothing else — no
// file, and no number in full (PRD §22). That is the right default and it
// leaves the person who photographed their own passport unable to see what it
// says. So the file and the number it yielded are kept here, in the browser
// storage of the mini-app, on the device that took the picture: the card can
// show them, and nothing had to start being stored on a server for that.
//
// IndexedDB rather than localStorage because the entries hold a file, and
// rather than a wrapper library because this is one store with four operations.

const DATABASE = 'expat-passport'
const VERSION = 2
const STORE = 'documents'
const FILES = 'files'

export interface VaultEntry {
  documentId: string
  number?: string
  file?: Blob
  fileName?: string
  savedAt: string
}

// available reports whether this browser will let anything be stored at all.
// A private window on some browsers refuses IndexedDB outright, and a card that
// simply shows less is a better answer than one that throws.
export function available(): boolean {
  try {
    return typeof indexedDB !== 'undefined'
  } catch {
    return false
  }
}

export interface LocalFile {
  path: string
  file: Blob
  savedAt: string
}

const unsaved = new Map<string, Blob>()

export async function putFile(path: string, file: Blob): Promise<void> {
  unsaved.set(path, file)

  const database = await open()

  if (!database) {
    return
  }

  await transact(
    database,
    'readwrite',
    (store) => store.put({ path, file, savedAt: new Date().toISOString() }),
    FILES,
  )
}

export async function getFile(path: string): Promise<Blob | undefined> {
  const database = await open()

  if (!database) {
    return unsaved.get(path)
  }

  const stored = await transact(
    database,
    'readonly',
    (store) => store.get(path) as IDBRequest<LocalFile | undefined>,
    FILES,
  )

  return stored?.file ?? unsaved.get(path)
}

export async function removeFolder(folder: string): Promise<void> {
  const prefix = `${folder}/`

  for (const path of [...unsaved.keys()]) {
    if (path.startsWith(prefix)) {
      unsaved.delete(path)
    }
  }

  const database = await open()

  if (!database) {
    return
  }

  await transact(
    database,
    'readwrite',
    (store) => store.delete(IDBKeyRange.bound(prefix, `${prefix}\uffff`)),
    FILES,
  )
}

export async function put(entry: Omit<VaultEntry, 'savedAt'>): Promise<void> {
  const database = await open()

  if (!database) {
    return
  }

  await transact(database, 'readwrite', (store) =>
    store.put({ ...entry, savedAt: new Date().toISOString() }),
  )
}

export async function get(documentId: string): Promise<VaultEntry | undefined> {
  const database = await open()

  if (!database) {
    return undefined
  }

  return transact(database, 'readonly', (store) => store.get(documentId) as IDBRequest<VaultEntry>)
}

export async function remove(documentId: string): Promise<void> {
  const database = await open()

  if (!database) {
    return
  }

  await transact(database, 'readwrite', (store) => store.delete(documentId))
}

export async function clear(): Promise<void> {
  const database = await open()

  if (!database) {
    return
  }

  unsaved.clear()

  await transact(database, 'readwrite', (store) => store.clear())
  await transact(database, 'readwrite', (store) => store.clear(), FILES)
}

function open(): Promise<IDBDatabase | undefined> {
  if (!available()) {
    return Promise.resolve(undefined)
  }

  return new Promise((resolve) => {
    const request = indexedDB.open(DATABASE, VERSION)

    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: 'documentId' })
      }

      if (!request.result.objectStoreNames.contains(FILES)) {
        request.result.createObjectStore(FILES, { keyPath: 'path' })
      }
    }

    request.onsuccess = () => resolve(request.result)

    // Storage that will not open is storage this app does without: every
    // caller treats an absent entry as «nothing kept on this device».
    request.onerror = () => resolve(undefined)
  })
}

function transact<T>(
  database: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
  name: string = STORE,
): Promise<T | undefined> {
  return new Promise((resolve) => {
    let request: IDBRequest<T>

    try {
      request = run(database.transaction(name, mode).objectStore(name))
    } catch {
      resolve(undefined)

      return
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(undefined)
  })
}
